/**
 * Production Execution V1 client — mirrors server/src/routes/productionOrders.ts
 * (/production-orders/:id/execution|start|completions|makeup|issue|close).
 * Server is authoritative: no stock / reservation math here.
 */
import { apiFetch, ApiError } from '../apiFetch'

export type ExecutionStatus = 'PLANNED' | 'READY' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'

export interface ExecutionState {
  plan_line_id: string
  status: ExecutionStatus
  /** NUMERIC string from server */
  completed_qty: string
  started_at?: string | null
  started_by?: string | null
  closed_at?: string | null
  closed_by?: string | null
}

export interface ReservationRow {
  id: string
  material_id: string
  requirement_line_id: string
  qty_reserved_base: string
  qty_consumed_base: string
  base_uom: string
  status: 'ACTIVE' | 'RELEASED' | 'CONSUMED' | string
}

export interface RequirementLine {
  requirement_line_id: string
  material_id: string | null
  material_code: string | null
  material_name: string
  uom: string
  required: number
  reserved_total: number
  consumed: number
  remaining_reserved: number
  shortage: number
  reservations: ReservationRow[]
}

export interface RequirementSummary {
  batch_id: string
  plan_line_id: string | null
  status: string
  lines: RequirementLine[]
}

export type MakeupAllocationStatus = 'RESERVED' | 'PARTIAL' | 'UNRESERVED'

export interface MakeupReservationRow {
  id: string
  material_id: string
  requirement_line_id: string
  qty_reserved_base: string
  qty_consumed_base: string
  remaining_qty_base: string
  base_uom: string
  status: 'ACTIVE' | 'RELEASED' | 'CONSUMED' | string
}

export interface MakeupRequest {
  id: string
  plan_line_id: string
  material_id: string
  qty_base: string
  base_uom: string
  reason: string
  status: string
  reserved_qty_base: string
  consumed_qty_base: string
  remaining_qty_base: string
  shortage_qty_base: string
  allocation_status: MakeupAllocationStatus
  reservation_ids: string[]
  linked_reservation_ids?: string[]
  reservations?: MakeupReservationRow[]
}

export interface IssueLineInput {
  reservation_id: string
  qty_base: number
  makeup_request_id?: string | null
}

export interface IssuePayload {
  purpose?: string
  note?: string | null
  lines: IssueLineInput[]
}

async function parse<T>(res: Response, fallback: string): Promise<T> {
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const err = body?.error
    throw new ApiError(
      err?.code ?? 'HTTP_ERROR',
      err?.message ?? `${fallback} (${res.status})`,
      res.status,
      err?.details,
    )
  }
  return body as T
}

const base = (id: string) => `/api/production-orders/${encodeURIComponent(id)}`

function post(url: string, body: unknown, idempotencyKey?: string): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey
  return apiFetch(url, { method: 'POST', headers, body: JSON.stringify(body ?? {}) })
}

export async function getExecutionApi(id: string): Promise<ExecutionState> {
  const res = await apiFetch(`${base(id)}/execution`)
  return (await parse<{ data: ExecutionState }>(res, 'Không thể tải trạng thái sản xuất')).data
}

export async function startProductionApi(id: string): Promise<ExecutionState> {
  const res = await post(`${base(id)}/start`, {})
  return (await parse<{ data: ExecutionState }>(res, 'Không thể bắt đầu sản xuất')).data
}

export async function recordCompletionApi(
  id: string,
  qty: number,
  idempotencyKey: string,
): Promise<{ state: ExecutionState }> {
  const res = await post(`${base(id)}/completions`, { qty, idempotency_key: idempotencyKey }, idempotencyKey)
  return (await parse<{ data: { state: ExecutionState } }>(res, 'Không thể ghi nhận hoàn thành')).data
}

export async function getProductionRequirementApi(id: string): Promise<RequirementSummary | null> {
  const res = await apiFetch(`${base(id)}/requirements`)
  return (await parse<{ data: RequirementSummary | null }>(res, 'Không thể tải nhu cầu vật tư')).data
}

export async function listProductionMakeupsApi(id: string): Promise<MakeupRequest[]> {
  const res = await apiFetch(`${base(id)}/makeup`)
  return (await parse<{ ok: true; data: MakeupRequest[] }>(res, 'Không thể tải danh sách bổ sung vật tư')).data ?? []
}

export async function requestMakeupApi(
  id: string,
  input: { material_id: string; qty_base: number; reason: string },
): Promise<MakeupRequest> {
  const res = await post(`${base(id)}/makeup`, input)
  return (await parse<{ data: MakeupRequest }>(res, 'Không thể tạo yêu cầu bổ sung vật tư')).data
}

export async function reserveMakeupAvailableApi(id: string, requestId: string): Promise<MakeupRequest> {
  const res = await post(`${base(id)}/makeup/${encodeURIComponent(requestId)}/reserve-available`, {})
  return (await parse<{ data: MakeupRequest }>(res, 'Không thể giữ hàng bổ sung')).data
}

export async function issueProductionMaterialApi(
  id: string,
  payload: IssuePayload,
  idempotencyKey: string,
): Promise<{ id?: string }> {
  const res = await post(`${base(id)}/issue`, { ...payload, idempotency_key: idempotencyKey }, idempotencyKey)
  return (await parse<{ data: { id?: string } }>(res, 'Không thể xuất vật tư')).data
}

export async function closeProductionApi(id: string): Promise<ExecutionState> {
  const res = await post(`${base(id)}/close`, {})
  return (await parse<{ data: ExecutionState }>(res, 'Không thể đóng lệnh sản xuất')).data
}
